import { StepHelpers } from "@shared/components/Stepper";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Separator } from "@shared/ui/separator";
import { Switch } from "@shared/ui/switch";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useBillData,
  useConsumerCreditNote,
  useConsumerLatestBill,
  useMeterdata,
  usePlanData,
  useUpdateConsumerSelection,
} from "../../hooks";
import ExpandablePanel from "./ExpandablePanel";
import StatusBadge from "./StatusBadge";
import { logEvent } from "@shared/analytics/analytics";

interface Step2AccountMetersPlansProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

const Step2AccountMetersPlans: React.FC<Step2AccountMetersPlansProps> = ({
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const { id, action = "add" } = useParams();
  const [lateFee, setLateFee] = useState(50);
  const [includeLateFee, setIncludeLateFee] = useState(true);
  const [includeOutstandingFee, setIncludeOutstandingFee] = useState(true);
  const [creditBalance, setCreditBalance] = useState(100);
  const [includeCreditBalance, setIncludeCreditBalance] = useState(true);
  const dataLoadedRef = useRef(false);
  const CURRENCY = useCurrency();
  const navigate = useNavigate();

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step 2 data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step 2 data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Helper function to get step 1 data (consumer selection)
  const getStep1Data = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(0); // Step 1 is at index 0
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return null;
    }
  }, [stepHelpers]);

  // Get billDraftId from step 1 data
  const step1Data = getStep1Data();
  const billId = step1Data?.billDraftId || id;

  const {
    data: billData,
    isLoading: isBillLoading,
    isError: isBillError,
  } = useBillData(billId);

  const { mutateAsync: updateConsumer, isPending: isSubmitting } =
    useUpdateConsumerSelection(billId);

  const consumerDataId = billData?.consumerData?.id;
  const remoteConsumerNumber = billData?.consumerData?.consumerNo;
  const invoiceStartDate = billData?.invoiceStartDate;
  const invoiceEndDate = billData?.invoiceEndDate;

  const { data: consumerCreditNote } = useConsumerCreditNote(consumerDataId);

  const { data: latestBillData } = useConsumerLatestBill(consumerDataId);
  console.log('bill data is here man latestBillData',latestBillData)

  const meterIds = latestBillData?.meters?.map((m: any) => m) || [];
  const planId = latestBillData?.remotePlanId;
console.log('bill data is here man meterIds',meterIds)
console.log('bill data is here man invoiceStartDate',invoiceStartDate)
console.log('bill data is here man invoiceEndDate',invoiceEndDate)


  const {
    data: planData,
    isLoading: isPlanLoading,
    isError: isPlanError,
  } = usePlanData(planId);

  const { data: meterData } = useMeterdata(
    meterIds,
    invoiceStartDate,
    invoiceEndDate
  );
  console.log("meterData", meterData);
  // Load saved data on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          // Restore state from saved data
          setLateFee(savedStepData.lateFee || 50);
          setIncludeLateFee(savedStepData.includeLateFee ?? true);
          setIncludeOutstandingFee(savedStepData.includeOutstandingFee ?? true);
          setCreditBalance(savedStepData.creditBalance || 100);
          setIncludeCreditBalance(savedStepData.includeCreditBalance ?? true);
        }

        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step 2 data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData]);

  // Auto-save when state changes
  useEffect(() => {
    if (dataLoadedRef.current) {
      saveStepData({
        lateFee,
        includeLateFee,
        includeOutstandingFee,
        creditBalance,
        includeCreditBalance,
        billData: billData
          ? {
              id: billData.id,
              consumerData: billData.consumerData,
              invoiceStartDate: billData.invoiceStartDate,
              invoiceEndDate: billData.invoiceEndDate,
              utilityService: billData.utilityService,
            }
          : null,
        lastUpdated: new Date().toISOString(),
      });
    }
  }, [
    lateFee,
    includeLateFee,
    includeOutstandingFee,
    creditBalance,
    includeCreditBalance,
    billData,
    // saveStepData,
  ]);

  const handleContinue = async () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    // Validate required data
    if (!latestBillData?.meters || latestBillData.meters.length === 0) {
      if (setValidationError) {
        setValidationError(
          "No meters found for this consumer. Please check the account setup."
        );
      }
      return;
    }

    if (!latestBillData?.id) {
      if (setValidationError) {
        setValidationError(
          "Consumer data is missing. Please go back and select a consumer."
        );
      }
      return;
    }

    try {
      const meterIds: { meter_id: number }[] =
        latestBillData?.meters?.map((meter: any) => meter) || [];

      const payload = {
        bill_data: {
          meter_ids: meterIds,
          consumer_data: {
            id: latestBillData.id,
            consumer_no: latestBillData.consumerNo,
            status: latestBillData.status,
            status_display: latestBillData.statusDisplay,
            is_e_bill: latestBillData.isEBill,
            is_papered_bill: latestBillData.isPaperedBill,
          },
          exclude_outstanding_balance: !includeOutstandingFee,
          include_credit: includeCreditBalance,
          // Additional configuration
          late_fee_settings: {
            include_late_fee: includeLateFee,
            late_fee_amount: lateFee,
          },
          credit_settings: {
            include_credit_balance: includeCreditBalance,
            credit_balance: creditBalance,
          },
        },
        step: {
          step_working_on: 1,
          total_steps: 3,
        },
      };

      console.log("Submitting step 2 payload:", payload);

      const response = await updateConsumer(payload);

      if (response) {
        // Save final step data with completion status
        saveStepData({
          lateFee,
          includeLateFee,
          includeOutstandingFee,
          creditBalance,
          includeCreditBalance,
          payload,
          response,
          meterData: meterData
            ? meterData.map((meter) => ({
                id: meter.id,
                meterNumber: meter.meterNumber,
                meterTypeDisplay: meter.meterTypeDisplay,
                productCode: meter.productCode,
                status: meter.status,
                previousReading: meter.previousReading,
                previousReadingDate: meter.previousReadingDate,
              }))
            : [],
          planData: planData
            ? {
                id: planData.id,
                planName: planData.planName,
                shortName: planData.shortName,
                description: planData.description,
                isActive: planData.isActive,
                billingFrequencyDisplay: planData.billingFrequencyDisplay,
                taxTypeDisplay: planData.taxTypeDisplay,
                category: planData.category,
                subcategory: planData.subcategory,
                utilityService: planData.utilityService,
              }
            : null,
          consumerAccountData: latestBillData
            ? {
                id: latestBillData.id,
                firstName: latestBillData.firstName,
                lastName: latestBillData.lastName,
                consumerNo: latestBillData.consumerNo,
                premise: latestBillData.premise,
                status: latestBillData.status,
                statusDisplay: latestBillData.statusDisplay,
                daysSinceLastBill: latestBillData.daysSinceLastBill,
                billData: latestBillData.billData,
              }
            : null,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        // Proceed to next step
        if (onNext) {
          onNext();
        }
      } else {
        if (setValidationError) {
          setValidationError(
            "Failed to update consumer selection. Please try again."
          );
        }
      }
    } catch (error) {
      console.error("Failed to continue from step 2:", error);
      if (setValidationError) {
        setValidationError(
          "An error occurred while processing. Please try again."
        );
      }
    }
  };

  const handleDraft = async () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    // Validate required data
    if (!latestBillData?.meters || latestBillData.meters.length === 0) {
      if (setValidationError) {
        setValidationError(
          "No meters found for this consumer. Please check the account setup."
        );
      }
      return;
    }

    if (!latestBillData?.id) {
      if (setValidationError) {
        setValidationError(
          "Consumer data is missing. Please go back and select a consumer."
        );
      }
      return;
    }

    try {
      const meterIds: { meter_id: number }[] =
        latestBillData?.meters?.map((meter: any) => meter) || [];

      const payload = {
        bill_data: {
          meter_ids: meterIds,
          consumer_data: {
            id: latestBillData.id,
            consumer_no: latestBillData.consumerNo,
            status: latestBillData.status,
            status_display: latestBillData.statusDisplay,
            is_e_bill: latestBillData.isEBill,
            is_papered_bill: latestBillData.isPaperedBill,
          },
          exclude_outstanding_balance: !includeOutstandingFee,
          include_credit: includeCreditBalance,
          // Additional configuration
          late_fee_settings: {
            include_late_fee: includeLateFee,
            late_fee_amount: lateFee,
          },
          credit_settings: {
            include_credit_balance: includeCreditBalance,
            credit_balance: creditBalance,
          },
        },
        step: {
          step_working_on: 1,
          total_steps: 3,
        },
      };

      const response = await updateConsumer(payload);

      if (response) {
        // Save final step data with completion status
        saveStepData({
          lateFee,
          includeLateFee,
          includeOutstandingFee,
          creditBalance,
          includeCreditBalance,
          payload,
          response,
          meterData: meterData
            ? meterData.map((meter) => ({
                id: meter.id,
                meterNumber: meter.meterNumber,
                meterTypeDisplay: meter.meterTypeDisplay,
                productCode: meter.productCode,
                status: meter.status,
                previousReading: meter.previousReading,
                previousReadingDate: meter.previousReadingDate,
              }))
            : [],
          planData: planData
            ? {
                id: planData.id,
                planName: planData.planName,
                shortName: planData.shortName,
                description: planData.description,
                isActive: planData.isActive,
                billingFrequencyDisplay: planData.billingFrequencyDisplay,
                taxTypeDisplay: planData.taxTypeDisplay,
                category: planData.category,
                subcategory: planData.subcategory,
                utilityService: planData.utilityService,
              }
            : null,
          consumerAccountData: latestBillData
            ? {
                id: latestBillData.id,
                firstName: latestBillData.firstName,
                lastName: latestBillData.lastName,
                consumerNo: latestBillData.consumerNo,
                premise: latestBillData.premise,
                status: latestBillData.status,
                statusDisplay: latestBillData.statusDisplay,
                daysSinceLastBill: latestBillData.daysSinceLastBill,
                billData: latestBillData.billData,
              }
            : null,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        navigate("/bx/history?tab=single&tab1=draft");
      } else {
        if (setValidationError) {
          setValidationError(
            "Failed to update consumer selection. Please try again."
          );
        }
      }
    } catch (error) {
      console.error("Failed to continue from step 2:", error);
      if (setValidationError) {
        setValidationError(
          "An error occurred while processing. Please try again."
        );
      }
    }
  };

  const handlePrevious = () => {
    // Save current state before going back
    saveStepData({
      lateFee,
      includeLateFee,
      includeOutstandingFee,
      creditBalance,
      includeCreditBalance,
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    });

    if (onPrevious) {
      onPrevious();
    }
  };
  useEffect(() => {
    logEvent("Bill Generation Late Fee Decision", {
      include: includeLateFee,
      fee: planData?.utilityServiceData?.utilityServiceCharges?.["Late Payment"]
        ?.fee,
    });
  }, [includeLateFee]);

  // Handle fee setting changes with auto-save
  const handleLateFeeChange = useCallback((value: number) => {
    setLateFee(value);
  }, []);

  const handleIncludeLateFeeChange = useCallback((checked: boolean) => {
    setIncludeLateFee(checked);
  }, []);

  const handleIncludeOutstandingFeeChange = useCallback((checked: boolean) => {
    setIncludeOutstandingFee(checked);
  }, []);

  const handleCreditBalanceChange = useCallback((value: number) => {
    setCreditBalance(value);
  }, []);

  const handleIncludeCreditBalanceChange = useCallback((checked: boolean) => {
    setIncludeCreditBalance(checked);
  }, []);

  const getCommonCharges = () => {
    const commonCharges =
      planData?.utilityServiceData?.extraCharges?.commonCharges;

    if (!commonCharges) return [];

    return Object.entries(commonCharges).map(([key, charge]) => {
      const c = charge as { basis: string; [key: string]: any };

      return {
        key,
        ...c,
        chargeLabel: formatChargeLabel(key),
        basisDisplay: capitalizeFirstLetter(c.basis),
      };
    });
  };

  const formatChargeLabel = (key: string) => {
    return key
      .replace(/_/g, " ")
      .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase());
  };

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Show loading state if critical data is not available
  if (isBillLoading || !billId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 -2 lue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading account information...</p>
        </div>
      </div>
    );
  }

  // Show error state if critical data failed to load
  if (isBillError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Failed to load account information.
          </p>
          <Button onClick={handlePrevious} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="card-subheading-text">
          Review account details, meters, and billing configuration before
          proceeding.
        </p>
      </div>

      <ExpandablePanel
        title="Account Information"
        statusBadge={
          <StatusBadge
            status={
              latestBillData?.statusDisplay === "ACTIVE" ? "success" : "warning"
            }
            text={latestBillData?.statusDisplay}
          />
        }
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="card-subheading-text">Consumer Name</Label>
              <div className="text-sm">
                {latestBillData?.firstName + " " + latestBillData?.lastName}
              </div>
            </div>
            <div>
              <Label className="card-subheading-text">Account No</Label>
              <div className="text-sm">{latestBillData?.consumerNo}</div>
            </div>
            <div>
              <Label className="card-subheading-text">Premise ID</Label>
              <div className="text-sm">{latestBillData?.premise}</div>
            </div>
            <div>
              <Label className="card-subheading-text">
                Registered Utilities
              </Label>
              <div className="text-sm">
                {billData?.utilityService?.join(", ")}
              </div>
            </div>
            <div>
              <Label className="card-subheading-text">Last Billing Date</Label>
              <div className="text-sm">
                {latestBillData?.billData?.createdDate ?? "NA"}
              </div>
            </div>
            <div>
              <Label className="card-subheading-text">
                Days Since Last Bill
              </Label>
              <div className="text-sm flex items-center gap-2">
                {latestBillData?.daysSinceLastBill ?? "NA"}
                {latestBillData?.daysSinceLastBill > 30 && (
                  <StatusBadge status="warning" text="Delayed" />
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="outstandingAmount">
                  Outstanding Amount ({CURRENCY})
                </Label>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="include-outstanding-fee" className="text-sm">
                    Include
                  </Label>
                  <Switch
                    id="include-outstanding-fee"
                    checked={includeOutstandingFee}
                    onCheckedChange={handleIncludeOutstandingFeeChange}
                  />
                </div>
              </div>
              <Input
                id="outstandingAmount"
                type="number"
                value={latestBillData?.billData?.outstandingBalance}
                disabled={true}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="lateFee">Late Fee ({CURRENCY})</Label>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="include-late-fee" className="text-sm">
                    Include
                  </Label>
                  <Switch
                    id="include-late-fee"
                    checked={includeLateFee}
                    onCheckedChange={handleIncludeLateFeeChange}
                  />
                </div>
              </div>
              <Input
                id="lateFee"
                type="number"
                value={
                  planData?.utilityServiceData?.utilityServiceCharges?.[
                    "Late Payment"
                  ]?.fee ?? "0"
                }
                onChange={(e) => handleLateFeeChange(Number(e.target.value))}
                disabled={true}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="creditBalance">
                  Credit Balance ({CURRENCY})
                </Label>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="include-credit" className="text-sm">
                    Include
                  </Label>
                  <Switch
                    id="include-credit"
                    checked={includeCreditBalance}
                    onCheckedChange={handleIncludeCreditBalanceChange}
                  />
                </div>
              </div>
              <Input
                id="creditBalance"
                type="number"
                value={consumerCreditNote?.credit}
                onChange={(e) =>
                  handleCreditBalanceChange(Number(e.target.value))
                }
                disabled={true}
              />
            </div>
          </div>
        </div>
      </ExpandablePanel>

      {/* Meter Information Panel */}
      <ExpandablePanel title="Meter Information">
        <div className="space-y-6">
          {Object.entries(
            meterData?.reduce((groups, meter) => {
              const productCode = meter.productCode;
              if (!groups[productCode]) {
                groups[productCode] = [];
              }
              groups[productCode].push(meter);
              return groups;
            }, {}) || {}
          ).map(([productCode, meters]) => (
            <div key={productCode}>
              {/* Product Code Header */}
              <div className="text-sm font-semibold text-gray-700 mb-2">
                {productCode} Meters
              </div>

              {/* Table for this product code */}
              <div className="rounded-md border overflow-hidden">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm text-muted-foreground">
                        Meter No/Device No
                      </th>
                      <th className="px-4 py-2 text-left text-sm text-muted-foreground">
                        Type
                      </th>
                      <th className="px-4 py-2 text-left text-sm text-muted-foreground">
                        Last Reading
                      </th>
                      <th className="px-4 py-2 text-left text-sm text-muted-foreground">
                        Last Date
                      </th>
                      <th className="px-4 py-2 text-left text-sm text-muted-foreground">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {meterData?.map((meter) => (
                      <tr key={meter.meterNumber}>
                        <td className="px-4 py-2 text-sm">
                          {meter.meterNumber}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {meter.meterTypeDisplay}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {meter.previousReading ?? "NA"}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {meter.previousReadingDate ?? "NA"}
                        </td>
                        <td className="px-4 py-2 text-sm ">
                          <StatusBadge
                            status={
                              meter.status === "ACTIVE" ? "success" : "warning"
                            }
                            text={meter.status}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </ExpandablePanel>

      {/* Plan & Tariff Details Panel */}
      <ExpandablePanel title="Plan Information">
        <div className="bg-white">
          {/* Header with Plan Name and Status */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {planData?.planName ?? "NA"}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge
                    status={planData?.isActive ? "success" : "warning"}
                    text={planData?.isActive ? "Active" : "Inactive"}
                  />
                  <span className="text-sm text-gray-500">
                    Valid: {planData?.startDate ?? "NA"}-
                    {planData?.validity ?? "NA"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Basic Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information Card */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 px-6 py-4  border-gray-200">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="card-heading-text">Basic Information</h3>
                </div>

                <div className="p-6 pt-0 space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm  text-gray-700 mb-1">
                        Plan Name
                      </label>
                      <div className="text-sm">
                        {planData?.planName ?? "NA"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm  text-gray-700 mb-1">
                        Short Name
                      </label>
                      <div className="text-sm">
                        {planData?.shortName ?? "NA"}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm  text-gray-700 mb-1">
                      Description
                    </label>
                    <div className="text-sm">
                      {planData?.description ?? "NA"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm  text-gray-700 mb-1">
                        Billing Frequency
                      </label>
                      <div className="text-sm text-gray-900">
                        {planData?.billingFrequencyDisplay ?? "NA"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm  text-gray-700 mb-1">
                        Tax Information
                      </label>
                      <div className="text-sm text-gray-900">
                        {planData?.taxTypeDisplay ?? "NA"}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm  text-gray-700 mb-2">
                      Consumer Categories
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs  bg-blue-100 text-blue-800">
                        {planData?.category ?? "NA"}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs  bg-gray-100 text-gray-800">
                        {planData?.subcategory ?? "NA"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Utility Services Details Card */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 py-4  border-gray-200">
                  <h3 className="card-heading-text">
                    Utility Services Details
                  </h3>
                </div>

                <div className="p-4 pt-0">
                  {planData?.utilityService?.map((service, index) => (
                    <div key={service} className="mb-4 last:mb-0">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                        <div className="flex items-center gap-3">
                          <svg
                            className="w-5 h-5 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                          <div>
                            <div className=" text-gray-900">{service}</div>
                            <div className="text-sm text-gray-500">
                              {planData?.utilityServiceData?.rate?.[service]
                                ?.rate?.rateTypeDisplay ?? "NA"}{" "}
                              •
                              {planData?.utilityServiceData?.rate?.[service]
                                ?.rate?.rateName ?? "NA"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {getCommonCharges().length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="px-6 py-4  border-gray-200">
                    <h3 className="card-heading-text">Common Charges</h3>
                  </div>

                  <div className="p-6">
                    <div className="space-y-3">
                      {getCommonCharges().map((charge) => (
                        <div
                          key={charge.key}
                          className="flex justify-between items-center py-3  border-gray-100 last:-0"
                        >
                          <div>
                            <div className=" text-gray-900">
                              {charge.chargeLabel}
                            </div>
                            <div className="text-sm text-gray-500">
                              ({charge.basisDisplay})
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">
                              {planData?.utilityServiceData?.extraCharges
                                ?.currency || CURRENCY}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 pt-4  border-gray-200">
                  <h3 className="card-heading-text">Plan Summary</h3>
                </div>

                <div className="p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs  bg-blue-100 text-blue-800">
                      Active
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Billing:</span>
                    <span className="text-sm  text-gray-900">
                      {planData?.billingFrequencyDisplay ?? "NA"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Tax:</span>
                    <span className="text-sm  text-gray-900">
                      {planData?.tax ?? "NA"}% (percentage)
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Services:</span>
                    <span className="text-sm  text-gray-900">
                      {planData?.utilityService?.length ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      Common Charges:
                    </span>
                    <span className="text-sm  text-gray-900">
                      {getCommonCharges().length ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Consumers:</span>
                    <span className="text-sm  text-gray-900">
                      {planData?.consumerCount ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Created:</span>
                    <span className="text-sm text-gray-900">
                      {planData?.createdDate ?? "NA"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      Last Modified:
                    </span>
                    <span className="text-sm text-gray-900">
                      {planData?.lastModifiedDate ?? "NA"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Plan Consumer Impact */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 px-6 py-4  border-gray-200">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <h3 className="card-heading-text">Plan Consumer Impact</h3>
                </div>

                <div className="p-4 pt-0 space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      Total Consumers:
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {planData?.consumerCount ?? "NA"}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 mt-0.5 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm text-gray-700">
                        All consumers are in the{" "}
                        {planData?.category?.toLowerCase() ?? "NA"}
                        category
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 mt-0.5 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-sm text-gray-700">
                        Changes would affect{" "}
                        {planData?.utilityService?.join(", ").toLowerCase() ??
                          "NA"}{" "}
                        service billing
                      </span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>Changes apply next billing cycle</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ExpandablePanel>

      <div className="flex justify-between pt-6">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={isSubmitting || isValidating}
        >
          Previous
        </Button>
        <div>
          <Button
            onClick={handleDraft}
            disabled={isSubmitting || isValidating}
            className="min-w-[200px] mr-5"
          >
            {isSubmitting || isValidating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 -2 border-white mr-2"></div>
                {isSubmitting ? "Saving..." : "Validating..."}
              </>
            ) : (
              "Save As Draft"
            )}
          </Button>
          <Button
            onClick={handleContinue}
            disabled={isSubmitting || isValidating}
            className="min-w-[200px]"
          >
            {isSubmitting || isValidating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 -2 border-white mr-2"></div>
                {isSubmitting ? "Processing..." : "Validating..."}
              </>
            ) : (
              "Continue to Readings & Consumption"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Step2AccountMetersPlans;
